import {
  CUSTOM_FIELD_TYPE,
  META_EXPRESSION_SCOPE,
  ORDER_TYPE,
  OrderStatus,
  PostgresTypes,
  TRIGGER_EVENT,
} from "fieldpro-tools";
import _ from "lodash";

import { IOption } from "model/application/components";
import { ITransformation } from "model/entities/Job";
import { IList } from "model/entities/List";
import { IWebhook } from "model/entities/Webhook";
import { IActivity } from "model/entities/Workflow";
import { activityNeedsCustomer } from "utils/businessUtils";

import { FieldTypes, getFieldsOfFieldType } from "./fieldOptions";

export interface ICustomField {
  tag: string;
  type: CUSTOM_FIELD_TYPE;
  options?: IOption[];
  list_id?: string;
}

const convertStringToCustomField = (input: string): CUSTOM_FIELD_TYPE => {
  let result: CUSTOM_FIELD_TYPE = CUSTOM_FIELD_TYPE.TEXT;
  switch (input.toLowerCase()) {
    case "string":
    case "text":
      result = CUSTOM_FIELD_TYPE.TEXT;
      break;
    case "boolean":
      result = CUSTOM_FIELD_TYPE.BOOLEAN;
      break;
    case "date":
      result = CUSTOM_FIELD_TYPE.DATE_PICKER;
      break;
    case "decimal":
    case "number":
      result = CUSTOM_FIELD_TYPE.DECIMAL;
      break;
    case "integer":
      result = CUSTOM_FIELD_TYPE.INTEGER;
      break;
    case "array": // will not get returned in ME
    case "object":
      break;
  }
  return result;
};

const convertToCustomField = (input: string): CUSTOM_FIELD_TYPE => {
  switch (input) {
    case PostgresTypes.BOOLEAN:
      return CUSTOM_FIELD_TYPE.BOOLEAN;
    case PostgresTypes.BIGINT:
    case PostgresTypes.INTEGER:
      return CUSTOM_FIELD_TYPE.INTEGER;
    case PostgresTypes.NUMERIC:
      return CUSTOM_FIELD_TYPE.DECIMAL;
    case PostgresTypes.TEXT:
      return CUSTOM_FIELD_TYPE.TEXT;
    case PostgresTypes.TIMESTAMP:
      return CUSTOM_FIELD_TYPE.DATE_PICKER;
    default:
      return input?.toUpperCase() as CUSTOM_FIELD_TYPE;
  }
};

interface IGetSchemaForMetaExpressionParams {
  scope?: META_EXPRESSION_SCOPE;
  currentResourceId?: string;
  currentSubResourceId?: string; // in case of a matrix
  numberIndex?: number; // if we are in an activity, only the previous questions should be selectable
  type?: CUSTOM_FIELD_TYPE;
  includeMatrix?: boolean;
  triggerEvent?: TRIGGER_EVENT;
  isSubCustomField?: boolean;
}

interface IGetSchemaForMetaExpression {
  // TODO: only "list" and "activity" should be required here -> we find the right ones in the caller
  activities: IActivity[];
  lists: IList[];
  transformations: ITransformation[];
  webhooks?: IWebhook[];
  params: IGetSchemaForMetaExpressionParams;
}

export const getSchemaForMetaExpression = (
  args: IGetSchemaForMetaExpression,
): ICustomField[] => {
  const { activities, lists, transformations, params, webhooks } = args;

  const {
    scope,
    currentResourceId,
    currentSubResourceId,
    numberIndex,
    type,
    includeMatrix = false, // default to false
    triggerEvent,
    isSubCustomField = false, // default to false
  } = params;

  let elements: ICustomField[] = [];
  if (!scope) return elements;
  if (
    scope === META_EXPRESSION_SCOPE.ACTIVITY_REPORT ||
    scope === META_EXPRESSION_SCOPE.WORKFLOW_REPORT
  ) {
    elements = getSchemaForReportMetaExpression(
      activities,
      currentResourceId,
      currentSubResourceId,
      numberIndex,
      lists,
      type,
      isSubCustomField,
    );
  } else if (
    scope === META_EXPRESSION_SCOPE.TRANSFORMATION ||
    scope === META_EXPRESSION_SCOPE.INTEG_RECORD
  ) {
    elements = getSchemaForActivityMetaExpression(
      transformations,
      currentResourceId,
    );
  } else if (scope === META_EXPRESSION_SCOPE.ITEM) {
    elements = getSchemaForItemMetaExpression(
      lists,
      currentResourceId,
      currentSubResourceId,
    );
  } else if (scope === META_EXPRESSION_SCOPE.CUSTOMER) {
    elements = getSchemaForCustomerMetaExpression(lists);
  } else if (scope === META_EXPRESSION_SCOPE.MATRIX_CELL) {
    elements = getSchemaForMatrixCellMetaExpression(
      activities,
      currentResourceId,
      currentSubResourceId,
    );
  } else if (scope === META_EXPRESSION_SCOPE.ITEM_CELL) {
    elements = getSchemaForItemCellMetaExpression(
      activities,
      lists,
      currentResourceId,
      currentSubResourceId,
    );
  } else if (scope === META_EXPRESSION_SCOPE.TRIGGER_EVENT) {
    elements = getSchemaForTriggerEventMetaExpression(
      triggerEvent,
      webhooks,
      lists,
      activities,
      currentResourceId,
    );
  }
  if (type) {
    elements = elements.filter((e) => {
      // sometimes, regardless of the type, we want to include matrix in the schema
      if (e.type === CUSTOM_FIELD_TYPE.MATRIX_ON_LIST && includeMatrix) {
        return true;
      }
      return e.type === type;
    });
  }
  return elements;
};

const getSchemaForItemCellMetaExpression = (
  activities: IActivity[],
  lists: IList[],
  currentResourceId?: string,
  currentSubResourceId?: string,
): ICustomField[] => {
  let result: ICustomField[] = [];
  // the current resource id is supposed to be an activity id.
  const activity = activities.find((a) => a.id === currentResourceId);
  if (activity) {
    const matrixQuestion = activity.questionnaire.questions.find(
      (q) => q.tag === currentSubResourceId,
    );
    if (matrixQuestion) {
      const list = lists.find((l) => l.id === matrixQuestion.list_id);
      if (list) {
        result = list.schema.map((att) => ({
          tag: att.column_tag,
          type: att.type,
          options: att.options,
          list_id: att.list_id,
        }));
      }
    }
  }
  return result;
};

const getSchemaForMatrixCellMetaExpression = (
  activities: IActivity[],
  currentResourceId?: string,
  currentSubResourceId?: string,
): ICustomField[] => {
  let result: ICustomField[] = [];
  // the current resource id is supposed to be an activity id.
  const activity = activities.find((a) => a.id === currentResourceId);
  if (activity) {
    const matrixQuestion = activity.questionnaire.questions.find(
      (q) => q.tag === currentSubResourceId && !q.deprecated,
    );

    if (matrixQuestion?.matrix) {
      result = _.filter(
        matrixQuestion?.matrix?.typed_questions,
        (question) => !question.deprecated,
      );
    }
  }
  return result;
};

const getSchemaForCustomerMetaExpression = (lists: IList[]): ICustomField[] => {
  let result: ICustomField[] = [];
  // we have to select the customer list
  const customerList = lists.find((l) => l.id === "customer");
  if (customerList) {
    result = customerList.schema.map((att) => ({
      tag: att.column_tag,
      type: att.type,
      options: att.options,
      list_id: att.list_id,
    }));
    result.push({
      tag: "_id",
      type: CUSTOM_FIELD_TYPE.TEXT,
      options: [],
      list_id: customerList.id,
    });
  }
  return result;
};

const getSchemaForOrderMetaExpression = (): ICustomField[] => {
  return [
    {
      tag: "id",
      type: CUSTOM_FIELD_TYPE.TEXT,
    },
    {
      tag: "status",
      type: CUSTOM_FIELD_TYPE.SINGLE_CHOICE,
      options: Object.keys(OrderStatus).map((s) => ({
        label: s,
        key: s,
      })),
    },
    {
      tag: "order_type",
      type: CUSTOM_FIELD_TYPE.SINGLE_CHOICE,
      options: Object.values(ORDER_TYPE).map((orderType) => ({
        label: orderType,
        key: orderType,
      })),
    },
    {
      tag: "customer_id",
      type: CUSTOM_FIELD_TYPE.TEXT,
    },
    {
      tag: "created_at",
      type: CUSTOM_FIELD_TYPE.DATE_PICKER,
    },
    {
      tag: "updated_at",
      type: CUSTOM_FIELD_TYPE.DATE_PICKER,
    },
    {
      tag: "sales_rep_id",
      type: CUSTOM_FIELD_TYPE.TEXT,
    },
    {
      tag: "user_persona",
      type: CUSTOM_FIELD_TYPE.TEXT,
    },
  ];
};

const getSchemaForItemMetaExpression = (
  lists: IList[],
  listId?: string,
  matrixTag?: string,
): ICustomField[] => {
  const list = lists.find((list) => list.id === listId);
  if (!list) {
    return [];
  }

  if (matrixTag) {
    const matrixSchema = list.schema.find(
      (attribute) => attribute.column_tag === matrixTag,
    );
    if (matrixSchema) {
      return _.compact(
        matrixSchema.matrix?.typed_questions.map((question) => ({
          tag: question.column_tag,
          type: question.type,
          options: question.options,
          list_id: question.list_id,
        })),
      );
    }
    return [];
  }

  const listSchemaOptions = list.schema.map((attribute) => ({
    tag: attribute.column_tag,
    type: attribute.type,
    options: attribute.options,
    list_id: attribute.list_id,
  }));

  return [
    { tag: "_id", type: CUSTOM_FIELD_TYPE.TEXT, options: [], list_id: list.id },
    ...listSchemaOptions,
  ];
};

export const getSchemaOfFieldType = (fieldType: FieldTypes): ICustomField[] => {
  const fieldConfig = getFieldsOfFieldType(fieldType);

  if (!fieldConfig) {
    return [];
  }

  const result: ICustomField[] = [];
  for (const [type, fields] of Object.entries(fieldConfig)) {
    fields.forEach((field) => {
      result.push({ tag: field, type: convertStringToCustomField(type) });
    });
  }
  return result;
};

export const getSchemaForTriggerEventMetaExpression = (
  event_type: TRIGGER_EVENT | undefined,
  webhooks?: IWebhook[],
  lists?: IList[],
  activities?: IActivity[],
  currentResourceId?: string,
): ICustomField[] => {
  switch (event_type) {
    case TRIGGER_EVENT.WEBHOOK_EVENT:
      if (!webhooks) return [];
      let result: ICustomField[] = [];
      const webhook = webhooks.find((w) => w.id === currentResourceId);
      if (webhook) {
        if (!webhook.response_schema) webhook.response_schema = [];
        result = webhook.response_schema.map((att) => ({
          tag: att.name,
          type: convertStringToCustomField(att.type),
        }));
      }
      return result;
    case TRIGGER_EVENT.FIELDPRO_CREATE_MOBILE_USER:
      return getSchemaOfFieldType(FieldTypes.USER_FIELDS);
    case TRIGGER_EVENT.FIELDPRO_CREATE_ITEM:
    case TRIGGER_EVENT.FIELDPRO_EDIT_ITEM:
    case TRIGGER_EVENT.FIELDPRO_VALIDATE_CREATED_ITEM:
    case TRIGGER_EVENT.FIELDPRO_EDIT_VALIDATED_ITEM:
      return getSchemaForItemMetaExpression(lists ?? [], currentResourceId);
    case TRIGGER_EVENT.FIELDPRO_CREATE_ITEM_UPLOAD:
      return [
        ...getSchemaForItemMetaExpression(lists ?? [], currentResourceId),
        {
          tag: "upload_id",
          type: CUSTOM_FIELD_TYPE.TEXT,
        },
      ];
    case TRIGGER_EVENT.FIELDPRO_CREATE_ACTIVITY_REPORT_UPLOAD:
      return getSchemaForReportMetaExpression(
        activities ?? [],
        currentResourceId,
      );
    case TRIGGER_EVENT.FIELDPRO_CREATE_ORDER:
    case TRIGGER_EVENT.FIELDPRO_EDIT_ORDER:
    case TRIGGER_EVENT.FIELDPRO_CANCEL_ORDER:
    case TRIGGER_EVENT.FIELDPRO_CONFIRM_ORDER:
    case TRIGGER_EVENT.FIELDPRO_ORDER_DELIVERY_CONFIRMED:
    case TRIGGER_EVENT.FIELDPRO_ORDER_DELIVERY_RETURNED:
    case TRIGGER_EVENT.FIELDPRO_ORDER_DELIVERED_CONTENT_UPDATED:
      return getSchemaForOrderMetaExpression();
    case TRIGGER_EVENT.FIELDPRO_ORDER_RETURN_APPROVED:
      return [
        ...getSchemaForOrderMetaExpression(),
        {
          tag: "order_return_id",
          type: CUSTOM_FIELD_TYPE.TEXT,
        },
      ];
    case TRIGGER_EVENT.FIELDPRO_ACTIVITY_REPORT_FULLY_SYNCED:
      const schema = getSchemaForReportMetaExpression(
        activities ?? [],
        currentResourceId,
      );
      return schema;
    default:
      return [];
  }
};

const getSchemaForActivityMetaExpression = (
  transformations: ITransformation[],
  currentResourceId?: string,
): ICustomField[] => {
  let result: ICustomField[] = [];
  const transformation = transformations.find(
    (t) => t.id === currentResourceId,
  );
  if (transformation) {
    result = transformation.destination_columns.map((e) => ({
      tag: e.column_name,
      type: convertToCustomField(e.column_type),
    }));
  }
  return result;
};

const getSchemaForReportMetaExpression = (
  activities: IActivity[],
  currentResourceId?: string,
  currentSubResourceId?: string,
  numberIndex?: number, // if we are in an activity, only the previous questions should be selectable
  lists?: IList[],
  type?: CUSTOM_FIELD_TYPE,
  isSubCustomField?: boolean,
) => {
  let result: ICustomField[] = [];
  // the current resource id is supposed to be an activity id
  const activity = _.find(activities, { id: currentResourceId });

  if (activity) {
    const questions = activity.questionnaire.questions;

    if (currentSubResourceId) {
      const matrixQuestion = _.find(questions, {
        tag: currentSubResourceId,
      });

      const matrixIndex = matrixQuestion?.index || -1;

      result = _.filter(
        activity.questionnaire.questions,
        (q) => numberIndex === undefined || q.index < matrixIndex,
      );

      if (matrixQuestion?.matrix && isSubCustomField) {
        result = _.filter(
          matrixQuestion.matrix.typed_questions,
          (q) => numberIndex === undefined || q.index < numberIndex,
        );

        if (type) {
          result = result.filter((e) => {
            return e.type === type;
          });
        }
      }
    } else {
      result = _.filter(
        activity.questionnaire.questions,
        (q) => numberIndex === undefined || q.index < numberIndex,
      );
    }

    if (activityNeedsCustomer(activity)) {
      const customerList = lists?.find((l) => l.id === "customer");
      const customerFields = customerList?.schema.map(
        (att): IOption<string> => ({
          key: att.column_tag,
          label: att.column_tag,
        }),
      );
      result.push({
        tag: "_selected_customer",
        type: CUSTOM_FIELD_TYPE.SINGLE_CHOICE,
        options: customerFields,
      });
    }

    result.push({
      tag: "_scope",
      type: CUSTOM_FIELD_TYPE.TEXT,
    });
  }
  return result;
};
